import type { ReactNode } from 'react';

import { styled } from '@backyard-ui/styles';
import type { StitchesCSS } from '@backyard-ui/styles';

import styles from './Col.styles';
import type { Breakpoints } from '../../../../types/Breakpoints';

import { filterFalsyValue } from '../../../utils';
import { isValidSize, getColumnSize, getColumnOffset } from '../Grid.utils';

export interface ColProps {
  /**
   * The amount of columns in each row
   */
  columns?: number;

  /**
   * The number of columns the component will take place.
   * The value might be a number for all breakpoints or
   * an object that will apply each valu
   * based on the key breakpoint, eg:
   * { md: 8 } will create a style for the md media query with a
   * size of 8 columns.
   */
  size?:
    | {
        [key in Breakpoints]?: number;
      }
    | number;

  /**
   * The number of columns the component will skip.
   * The value might be a number for all breakpoints or
   * an object that will apply each valu
   * based on the key breakpoint, eg:
   * { md: 8 } will create a style for the md media query with an
   * offset of 8 columns. Accepts negative values as well.
   */
  offset?:
    | {
        [key in Breakpoints]?: number;
      }
    | number;

  /**
   * If true the component will occupy remaining columns
   * by setting 'flex-grow: 1'
   */
  isGrow?: boolean;

  /**
   * Stitches `css` func
   */
  css?: StitchesCSS;

  /**
   * The child nodes
   */
  children: ReactNode;
}

const ColEl = styled('div', styles);

const Col = (props: ColProps) => {
  const { size, offset, columns, ...rest } = props;

  if (!isValidSize(size)) {
    return null;
  }

  const styles = filterFalsyValue({
    ...getColumnSize(size, columns),
    ...getColumnOffset(offset, columns),
  });

  return <ColEl css={{ ...styles }} {...rest} />;
};

Col.displayName = 'Col';

export default Col;
